import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { PutCustomerSettingsPractitionerDocument } from '../../../../../../graphql/camel'
import useCamelMutation from '../../../../../../hooks/useCamelMutation'
import useSubmitFormOnDirty from '../../../../../../hooks/useSubmitFormOnDirty'
import type { PractitionerSettings } from '../../../../../../lib/practitioner-settings/types'
import { inboundPatientShouldBeTrue } from '../../../../../../lib/practitioner-settings/utils'
import { logAndCaptureException } from '../../../../../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../../../../../utils/graphqlTools'
import { MutationErrorAlert } from '../../../../../MutationErrorAlert'
import DispensingOptionInPractice from './DispensingOptionInPractice'
import DispensingOptionVirtualDispensary from './DispensingOptionVirtualDispensary'

export interface DispensingOptionsProps {
  settings: PractitionerSettings
}

const Container = styled.div`
  --dispensing-option-spacing: 32px;
  font-size: 14px;
  p {
    font-size: inherit;
    // produces paragraph spacing ~75% of line-height (1.43)
    margin-bottom: 1.1em;
  }
  @media (min-width: 768px) {
    max-width: 688px;
  }
`

const StyledDispensingOptionInPractice = styled(DispensingOptionInPractice)`
  margin-bottom: var(--dispensing-option-spacing);
`

const StyledDispensingOptionVirtualDispensary = styled(DispensingOptionVirtualDispensary)`
  margin-bottom: var(--dispensing-option-spacing);
`

interface FormValues {
  inboundPatient: boolean
}

const DispensingOptions: FC<DispensingOptionsProps> = ({ settings }) => {
  const alertRef = useRef<HTMLDivElement>(null)

  const [putCustomerSettingsPractitioner] = useCamelMutation(
    PutCustomerSettingsPractitionerDocument,
  )

  const { control, errors, formState, handleSubmit, register, reset, setError } =
    useForm<FormValues>({ mode: 'onChange' })

  const onSubmit = useCallback(
    async ({ inboundPatient }: FormValues) => {
      const result = await putCustomerSettingsPractitioner({
        variables: {
          customerSettingsPractitionerUpdateInput: {
            optedOutOfInboundPatient: !inboundPatient,
          },
        },
      })
        .then(checkAndDiscardQueryResponse)
        .then(() => ({ error: undefined }))
        .catch((err) => {
          const error = err instanceof Error ? err : new Error(`${err}`)
          logAndCaptureException(error)
          return { error }
        })
      if (result.error) {
        setError('inboundPatient', { message: result.error.message, type: 'server' })
      }
    },
    [putCustomerSettingsPractitioner, setError],
  )

  const { optedOutOfInboundPatient, optedOutOfDispensary } = settings.practitioner
  const inboundPatientDefaultValue = useMemo(
    () => inboundPatientShouldBeTrue({ optedOutOfDispensary, optedOutOfInboundPatient }),
    [optedOutOfDispensary, optedOutOfInboundPatient],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        inboundPatient: inboundPatientDefaultValue,
      }),
      [inboundPatientDefaultValue],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
    submitOnDirty: true,
  })

  useEffect(() => {
    if (formState.isSubmitting) {
      return
    }
  }, [formState])

  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [])

  const hasServerError = !!errors.inboundPatient && errors.inboundPatient.type === 'server'

  return (
    <Container>
      {hasServerError && (
        <div ref={alertRef}>
          <MutationErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)}>
            An error occurred updating your settings, and we have been notified.
          </MutationErrorAlert>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledDispensingOptionInPractice control={control} />
        <StyledDispensingOptionVirtualDispensary
          control={control}
          isSubmitting={formState.isSubmitting}
          register={register}
          settings={settings}
        />
      </form>
    </Container>
  )
}

export default DispensingOptions
